import type * as ngRouter from '@angular/router'
import * as ngrxRouterStore from '@ngrx/router-store'

import { EncodedQueryParam } from '../models'

/**
 * {@link https://ngrx.io/docs State} of this router
 *
 * __NOTE:__ since we are using {@link ngrxRouterStore.RouterReducerState}, most
 * of the content is in {@link State.Payload} (through
 * {@link State.Payload.Serializer})
 */
export interface State
  extends ngrxRouterStore.RouterReducerState<State.Payload> {
  /**
   * Used to determine if any navigation requests are currently in progress
   */
  openRequests: number
}
export namespace State {
  export const INITIAL: State = {
    navigationId: -1,
    openRequests: 0,
    state: {
      encodedQueryParamsFullObject: {},
      queryParams: {},
      routeParams: {},
      // NOTE: ngrxRouterStore expects the URL to only contain the path
      url: window.location.pathname,
    },
  }

  /**
   * Our custom router state, managed using {@link Payload.Serializer}
   */
  export interface Payload extends ngrxRouterStore.BaseRouterStoreState {
    fragment?: string
    routeParams: Partial<Record<string, string>>
    queryParams: Partial<Record<string, string>>
    /**
     * @see {@link EncodedQueryParam.FullObject}
     */
    encodedQueryParamsFullObject: EncodedQueryParam.FullObject
  }
  export namespace Payload {
    /**
     * Our {@link https://ngrx.io/guide/router-store/configuration#custom-router-state-serializer custom} router state serializer
     *
     *  @see {@link ngrxRouterStore.RouterStateSerializer}, {@link Payload}
     */
    export class Serializer extends ngrxRouterStore.RouterStateSerializer<Payload> {
      /** @inheritdoc */
      public serialize(
        routerStateSnapshot: ngRouter.RouterStateSnapshot,
      ): Payload {
        return {
          encodedQueryParamsFullObject: EncodedQueryParam.FullObject.decode(
            (
              routerStateSnapshot.root.queryParams[
                EncodedQueryParam.KEY
              ] as unknown
            )?.toString(),
          ),
          fragment: routerStateSnapshot.root.fragment ?? undefined,
          queryParams: routerStateSnapshot.root.queryParams,
          routeParams: this._collectAllRouteParams(routerStateSnapshot),
          url: routerStateSnapshot.url,
        }
      }

      /**
       * @see {@link https://ngrx.io/guide/router-store/selectors#extracting-all-params-in-the-current-route} or {@link ngRouter.ActivatedRouteSnapshot.params}
       */
      private _collectAllRouteParams(
        routerStateSnapshot: ngRouter.RouterStateSnapshot,
      ): Payload['routeParams'] {
        const routeParams: Payload['routeParams'] = {}
        const stack: ngRouter.ActivatedRouteSnapshot[] = [
          routerStateSnapshot.root,
        ]
        let currentRoute: ngRouter.ActivatedRouteSnapshot | undefined
        while ((currentRoute = stack.pop())) {
          Object.assign(routeParams, currentRoute.params)
          stack.push(...currentRoute.children)
        }
        // TODO: figure out if this is still needed? Or does Angular decode
        // automatically?

        // We need to decode any route parameters that were encoded
        for (const key in routeParams) {
          const value = routeParams[key]
          if (value) {
            routeParams[key] = decodeURIComponent(value)
          }
        }
        return routeParams
      }
    }
  }
}
